// Timeline Context Menu Infrastructure Adapters

import type { 
  ClipProvider, 
  AssetProvider, 
  SceneProvider,
  AssetData,
  AssetStateUpdate
} from '@/modules/timelineContextMenu/types';
import type { ClipServiceImpl } from '@/modules/databaseService/clips';

export class TimelineClipAdapter implements ClipProvider {
  constructor(private readonly clipService: ClipServiceImpl) {}

  async fetchAllSceneClips(sceneId: string) {
    const clips = await this.clipService.fetchAllSceneClips(sceneId);
    return clips.map(clip => ({
      instanceId: clip.clip_instance_id || '',
      assetId: clip.asset_instance_id || '',
      instanceStartTime: clip.clip_instance_begin_time || 0,
      instanceDuration: (clip.clip_instance_end_time || 0) - (clip.clip_instance_begin_time || 0),
      typeId: clip.clip_type_id || ''
    }));
  }

  async deleteClipInstance(clipInstanceId: string): Promise<void> {
    await this.clipService.deleteClipInstance(clipInstanceId);
  }
}

export class TimelineAssetAdapter implements AssetProvider {
  constructor(
    private readonly assetFinder: (assetInstanceId: string) => AssetData | undefined,
    private readonly assetUpdater: (assetInstanceId: string, updates: AssetStateUpdate) => Promise<void>
  ) {}

  async findAssetById(assetInstanceId: string): Promise<AssetData> {
    const asset = this.assetFinder(assetInstanceId);
    if (!asset) {
      throw new Error(`Asset not found: ${assetInstanceId}`);
    }
    return asset;
  }

  async updateAssetState(assetInstanceId: string, updates: AssetStateUpdate): Promise<void> {
    await this.assetUpdater(assetInstanceId, updates);
  }
}

export class TimelineSceneAdapter implements SceneProvider {
  constructor(
    private readonly sceneInstanceGetter: () => { scene_instance_id: string },
    private readonly sceneIdGetter: () => string
  ) {}

  getSceneInstance(): { scene_instance_id: string } {
    return this.sceneInstanceGetter();
  }

  getSceneId(): string {
    return this.sceneIdGetter();
  }
}
